import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

interface DeleteStreamDialogProps {
  streamId: string | null;
  onClose: () => void;
}

export function DeleteStreamDialog({ streamId, onClose }: DeleteStreamDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deleteStream = useMutation({
    mutationFn: async (id: string) => {
      try {
        const { data: studentsInStream, error: studentsError } = await supabase
          .from('students')
          .select('id, surname, other_names')
          .eq('stream', id);

        if (studentsError) throw studentsError;

        if (studentsInStream && studentsInStream.length > 0) {
          const studentNames = studentsInStream
            .map(s => `${s.surname} ${s.other_names || ''}`.trim())
            .join(', ');
          throw new Error(`This stream has ${studentsInStream.length} student(s) assigned to it: ${studentNames}. Please reassign these students first.`);
        }

        const { error } = await supabase
          .from('class_streams')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
      } catch (error: any) {
        console.error('Error deleting stream:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class_streams'] });
      onClose();
      toast({
        title: "Success",
        description: "Stream has been successfully deleted",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting stream:', error);
      toast({
        title: "Error Deleting Stream",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      onClose();
    },
  });

  const handleDelete = () => {
    if (streamId) {
      deleteStream.mutate(streamId);
    }
  };

  return (
    <AlertDialog open={!!streamId} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Stream</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this stream? This action cannot be undone.
            Make sure no students are assigned to this stream before proceeding.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={deleteStream.isPending}
          >
            {deleteStream.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}